// v2.22.2: picking many files, a whole folder, or dropping them. Chrome/Edge give file/folder handles (File System
// Access API) that the queue stores, so after the browser was closed one "Izinkan" is enough; other browsers use
// <input multiple>, webkitdirectory and webkitGetAsEntry (no handles: the same file is picked again later).
// entri = {file, handle?, akar?, jalur?}: jalur = path inside the chosen/dropped folder ("sub/a.png"), akar = that
// folder's handle (one permission covers all its files).
export const MAKS_KUMPUL = 300; // files read from one folder at most (the queue takes 100 per addition)

export const adaPegangan = () => typeof window !== "undefined" && typeof window.showOpenFilePicker === "function";
export const adaPilihFolder = () => typeof window !== "undefined" && typeof window.showDirectoryPicker === "function";

// accept list for <input accept> and the picker: jenis ["jpg","tif",…] -> [".jpg", ".jpeg", ".tif", ".tiff", …]
export function ekstensiDiterima(jenis) {
  const out = [];
  for (const j of jenis || []) out.push(...(j === "jpg" ? [".jpg", ".jpeg"] : j === "tif" ? [".tif", ".tiff"] : [`.${j}`]));
  return out;
}

const batal = (e) => e?.name === "AbortError"; // the user closed the picker

export async function pilihBanyak(jenis) {
  try {
    const hs = await window.showOpenFilePicker({ multiple: true, excludeAcceptAllOption: false,
      types: [{ description: "File desain", accept: { "application/octet-stream": ekstensiDiterima(jenis) } }] });
    return Promise.all(hs.map(async (handle) => ({ file: await handle.getFile(), handle })));
  } catch (e) { if (batal(e)) return []; throw e; }
}

async function isiFolder(dir, awalan, out, akar) {
  for await (const [nama, h] of dir.entries()) {
    if (out.length >= MAKS_KUMPUL) return;
    const jalur = awalan ? `${awalan}/${nama}` : nama;
    if (h.kind === "file") out.push({ file: await h.getFile(), handle: h, akar, jalur });
    else await isiFolder(h, jalur, out, akar);
  }
}

export async function pilihFolder() {
  let dir;
  try { dir = await window.showDirectoryPicker({ mode: "read" }); } catch (e) { if (batal(e)) return { entri: [], nama: null }; throw e; }
  const out = [];
  await isiFolder(dir, "", out, dir);
  return { entri: out, nama: dir.name, lebih: out.length >= MAKS_KUMPUL };
}

// <input multiple> / <input webkitdirectory>
export function dariInput(fileList) {
  return Array.from(fileList || []).slice(0, MAKS_KUMPUL).map((file) => {
    const rel = file.webkitRelativePath || "";
    return { file, jalur: rel.includes("/") ? rel.slice(rel.indexOf("/") + 1) : "" };
  });
}

function bacaEntri(entry, awalan, out) { // webkitGetAsEntry (Firefox, Safari)
  if (out.length >= MAKS_KUMPUL) return Promise.resolve();
  const jalur = awalan ? `${awalan}/${entry.name}` : entry.name;
  if (entry.isFile) return new Promise((res) => entry.file((file) => { out.push({ file, jalur: awalan ? jalur : "" }); res(); }, () => res()));
  return new Promise((res) => {
    const r = entry.createReader();
    const baca = () => r.readEntries(async (list) => {
      if (!list.length || out.length >= MAKS_KUMPUL) { res(); return; }
      for (const e of list) await bacaEntri(e, jalur, out);
      baca();
    }, () => res());
    baca();
  });
}

// a drop: the handles/entries must be asked for during the event itself, before anything is awaited
export async function dariDrop(dataTransfer) {
  const items = Array.from(dataTransfer?.items || []).filter((i) => i.kind === "file");
  // all three are taken now: a handle can come back empty (e.g. dragged from the browser's download bar)
  const janji = items.map((i) => ({ h: typeof i.getAsFileSystemHandle === "function" ? i.getAsFileSystemHandle() : null,
    e: i.webkitGetAsEntry?.(), f: i.getAsFile() }));
  const out = [];
  for (const j of janji) {
    if (out.length >= MAKS_KUMPUL) break;
    const h = j.h ? await j.h.catch(() => null) : null;
    if (h?.kind === "file") out.push({ file: await h.getFile(), handle: h });
    else if (h?.kind === "directory") await isiFolder(h, "", out, h);
    else if (j.e?.isDirectory) {
      await bacaEntri(j.e, "", out);
    } else if (j.f) out.push({ file: j.f });
  }
  if (!items.length) for (const file of Array.from(dataTransfer?.files || []).slice(0, MAKS_KUMPUL)) out.push({ file });
  return { entri: out, lebih: out.length >= MAKS_KUMPUL };
}

async function izin(h) {
  if ((await h.queryPermission?.({ mode: "read" })) === "granted") return true;
  return (await h.requestPermission?.({ mode: "read" })) === "granted";
}

// a stored row back to a File (after "Izinkan"); null when permission is refused or the file is gone.
// sudah = Map of folder handles already allowed in this click (one permission per folder)
export async function fileDariSimpanan(rec, sudah = []) {
  if (rec.akar) {
    let dir = null;
    for (const a of sudah) if (await a.isSameEntry?.(rec.akar).catch(() => false)) { dir = a; break; }
    if (!dir) { if (!(await izin(rec.akar))) return null; dir = rec.akar; sudah.push(dir); }
    const bagian = String(rec.jalur || rec.nama).split("/");
    for (const b of bagian.slice(0, -1)) dir = await dir.getDirectoryHandle(b);
    return (await dir.getFileHandle(bagian[bagian.length - 1])).getFile();
  }
  if (rec.handle) { if (!(await izin(rec.handle))) return null; return rec.handle.getFile(); }
  return null;
}
